import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { apiFetch } from "@/api/client";
import { setAnalyticsUser } from "@/utils/analytics";
import {
  trackAdminImpersonationStarted,
  trackAdminImpersonationStopped,
  trackEmailVerified,
  trackLogin,
  trackLogin2faVerified,
  trackLogout,
  trackSignUp,
} from "@/utils/googleAdsEvents";

type UserRole = "eleve" | "tuteur" | "staff";

type User = {
  id: string;
  name: string;
  firstName?: string;
  familyName?: string;
  isParentForChild?: boolean;
  childFirstName?: string;
  childFamilyName?: string;
  parentEmail?: string;
  parentEmailNotificationMode?: "all" | "important_only" | "none";
  parentPhone?: string;
  studentPhone?: string;
  email: string;
  role: UserRole;
  staffType?: "responsable_commercial";
  staffCitySlugs?: string[];
  admin?: boolean;
  avatarUrl?: string;
  emailVerified?: boolean;
  twoFactorEnabled?: boolean;
  impersonationActive?: boolean;
  impersonationAdminId?: string;
  onboardingCompleted?: boolean;
};

type AuthContextValue = {
  user: User | null;
  isAuthenticated: boolean;
  isAuthReady: boolean;
  login: (
    email: string,
    password: string,
    rememberMe: boolean,
  ) => Promise<{
    twoFactorRequired: boolean;
    loginToken?: string;
    emailVerificationRequired?: boolean;
    email?: string;
  }>;
  verifyTwoFactor: (
    loginToken: string,
    code: string,
    rememberMe: boolean,
  ) => Promise<void>;
  register: (payload: {
    firstName: string;
    familyName: string;
    isParentForChild?: boolean;
    childFirstName?: string;
    childFamilyName?: string;
    parentEmail?: string;
    parentEmailNotificationMode?: "all" | "important_only" | "none";
    parentPhone?: string;
    studentPhone?: string;
    email: string;
    password: string;
    role: "eleve" | "tuteur";
    inviteToken?: string;
    platformInviteToken?: string;
    acceptedCgu: boolean;
  }) => Promise<{ emailVerificationRequired: boolean; email?: string }>;
  verifyEmail: (
    email: string,
    code: string,
    rememberMe: boolean,
  ) => Promise<User>;
  requestEmailVerification: (email: string) => Promise<void>;
  updateRole: (role: UserRole) => void;
  updateUser: (payload: Partial<User>) => void;
  impersonateUser: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  isImpersonating: boolean;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = "fs-user";
const TOKEN_KEY = "fs-token";
const IMPERSONATION_STORAGE_KEY = "fs-impersonation-admin-session";

type AuthResponse = {
  token: string;
  user: User;
};

type StoredSession = {
  token: string;
  user: User;
};

const parseStoredSession = (raw: string | null): StoredSession | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
};

const readImpersonationBackup = (): StoredSession | null => {
  if (typeof window === "undefined") return null;

  return (
    parseStoredSession(sessionStorage.getItem(IMPERSONATION_STORAGE_KEY)) ||
    parseStoredSession(localStorage.getItem(IMPERSONATION_STORAGE_KEY))
  );
};

const clearImpersonationBackup = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
};

type TwoFactorResponse = {
  twoFactorRequired: true;
  loginToken: string;
};

type EmailVerificationResponse = {
  emailVerificationRequired: true;
  email: string;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const parseStoredUser = (raw: string | null) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw) as User;
      } catch {
        return null;
      }
    };

    const localToken = localStorage.getItem(TOKEN_KEY);
    const localStored = parseStoredUser(localStorage.getItem(STORAGE_KEY));
    const sessionToken = sessionStorage.getItem(TOKEN_KEY);
    const sessionStored = parseStoredUser(sessionStorage.getItem(STORAGE_KEY));

    if (!sessionToken && sessionStored) {
      sessionStorage.removeItem(STORAGE_KEY);
    }

    const storageWithToken = localToken
      ? localStorage
      : sessionToken
        ? sessionStorage
        : null;
    const restoredUser = localToken
      ? localStored
      : sessionToken
        ? sessionStored
        : null;

    if (!localToken && !sessionToken) {
      clearImpersonationBackup();
    }

    if (restoredUser && !restoredUser.impersonationActive) {
      clearImpersonationBackup();
    }

    if (restoredUser) {
      setUser(restoredUser);
      let isCancelled = false;

      apiFetch<{ user: User }>("/api/auth/me")
        .then((response) => {
          if (isCancelled) return;
          storageWithToken?.setItem(STORAGE_KEY, JSON.stringify(response.user));
          setUser(response.user);
        })
        .catch(() => {
          // Keep restored session if refresh fails temporarily.
        })
        .finally(() => {
          if (isCancelled) return;
          setIsAuthReady(true);
        });

      return () => {
        isCancelled = true;
      };
    }

    if (!storageWithToken) {
      setIsAuthReady(true);

      return;
    }

    let isCancelled = false;

    apiFetch<{ user: User }>("/api/auth/me")
      .then((response) => {
        if (isCancelled) return;
        storageWithToken.setItem(STORAGE_KEY, JSON.stringify(response.user));
        setUser(response.user);
      })
      .catch(() => {
        if (isCancelled) return;
        setUser(null);
      })
      .finally(() => {
        if (isCancelled) return;
        setIsAuthReady(true);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    const onSessionExpired = () => setUser(null);

    window.addEventListener("auth:session-expired", onSessionExpired);

    return () =>
      window.removeEventListener("auth:session-expired", onSessionExpired);
  }, []);

  useEffect(() => {
    setAnalyticsUser({
      id: user?.id,
      role: user?.role,
      admin: user?.admin,
      emailVerified: user?.emailVerified,
      impersonationActive: user?.impersonationActive,
      isAuthenticated: Boolean(user),
    });
  }, [user]);

  const persistSession = (
    payload: AuthResponse,
    rememberMe: boolean,
    targetStorage?: "local" | "session",
  ) => {
    const storageType = targetStorage || (rememberMe ? "local" : "session");

    if (storageType === "local") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload.user));
      localStorage.setItem(TOKEN_KEY, payload.token);
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload.user));
      sessionStorage.setItem(TOKEN_KEY, payload.token);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
    }
  };

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean,
  ) => {
    const response = await apiFetch<
      AuthResponse | TwoFactorResponse | EmailVerificationResponse
    >("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, rememberMe }),
    });

    if ("emailVerificationRequired" in response) {
      return {
        twoFactorRequired: false,
        emailVerificationRequired: true,
        email: response.email,
      };
    }
    if ("twoFactorRequired" in response) {
      return { twoFactorRequired: true, loginToken: response.loginToken };
    }
    persistSession(response, rememberMe);
    setUser(response.user);
    trackLogin({ method: "password", role: response.user.role });

    return { twoFactorRequired: false };
  };

  const register = async (payload: {
    firstName: string;
    familyName: string;
    isParentForChild?: boolean;
    childFirstName?: string;
    childFamilyName?: string;
    parentEmail?: string;
    parentEmailNotificationMode?: "all" | "important_only" | "none";
    parentPhone?: string;
    studentPhone?: string;
    email: string;
    password: string;
    role: "eleve" | "tuteur";
    inviteToken?: string;
    platformInviteToken?: string;
    acceptedCgu: boolean;
  }) => {
    const response = await apiFetch<AuthResponse | EmailVerificationResponse>(
      "/api/auth/register",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if ("emailVerificationRequired" in response) {
      return { emailVerificationRequired: true, email: response.email };
    }
    persistSession(response, true);
    setUser(response.user);
    trackSignUp({ method: "password", role: response.user.role });

    return { emailVerificationRequired: false };
  };

  const verifyEmail = async (
    email: string,
    code: string,
    rememberMe: boolean,
  ) => {
    const response = await apiFetch<AuthResponse>("/api/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ email, code, rememberMe }),
    });

    persistSession(response, rememberMe);
    setUser(response.user);
    trackEmailVerified({ role: response.user.role });

    return response.user;
  };

  const requestEmailVerification = async (email: string) => {
    await apiFetch<{ sent: boolean }>("/api/auth/verify-email/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  };

  const verifyTwoFactor = async (
    loginToken: string,
    code: string,
    rememberMe: boolean,
  ) => {
    const response = await apiFetch<AuthResponse>("/api/auth/2fa/verify", {
      method: "POST",
      body: JSON.stringify({ loginToken, code, rememberMe }),
    });

    persistSession(response, rememberMe);
    setUser(response.user);
    trackLogin2faVerified({ role: response.user.role });
  };

  const impersonateUser = async (userId: string) => {
    if (!user) return;

    const sourceStorage =
      localStorage.getItem(TOKEN_KEY) != null ? "local" : "session";
    const sourceToken =
      sourceStorage === "local"
        ? localStorage.getItem(TOKEN_KEY)
        : sessionStorage.getItem(TOKEN_KEY);

    if (!sourceToken) {
      throw new Error("Session administrateur introuvable.");
    }

    const response = await apiFetch<AuthResponse>(
      `/api/admin/users/${userId}/impersonate`,
      {
        method: "POST",
      },
    );

    const backup: StoredSession = {
      token: sourceToken,
      user,
    };

    if (sourceStorage === "local") {
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(backup));
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    } else {
      sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(backup));
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }

    persistSession(response, sourceStorage === "local", sourceStorage);
    setUser(response.user);
    trackAdminImpersonationStarted({
      impersonated_user_role: response.user.role,
    });
  };

  const stopImpersonation = async () => {
    const backup = readImpersonationBackup();

    if (!backup) {
      throw new Error("Session admin introuvable. Reconnectez-vous.");
    }

    const response = await apiFetch<AuthResponse>(
      "/api/auth/impersonation/stop",
      {
        method: "POST",
      },
    );

    const storageType =
      localStorage.getItem(IMPERSONATION_STORAGE_KEY) != null
        ? "local"
        : "session";

    persistSession(response, storageType === "local", storageType);
    clearImpersonationBackup();
    setUser(response.user);
    trackAdminImpersonationStopped({ role: response.user.role });
  };

  const logout = () => {
    trackLogout();
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    setUser(null);
  };

  const updateRole = (role: UserRole) => {
    if (!user) return;
    if (user.role === role) return;
    const updated = { ...user, role };
    const storage = localStorage.getItem(TOKEN_KEY)
      ? localStorage
      : sessionStorage;

    storage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setUser(updated);
  };

  const updateUser = (payload: Partial<User>) => {
    if (!user) return;
    const updated = { ...user, ...payload };
    const serialized = JSON.stringify(updated);

    // Skip the state update (and its new object reference) when nothing
    // actually changed. Without this guard every updateUser call — e.g. the
    // /api/auth/me refresh on the onboarding screen — allocates a fresh user
    // object and re-triggers any effect that depends on `user`.
    if (serialized === JSON.stringify(user)) return;
    const storage = localStorage.getItem(TOKEN_KEY)
      ? localStorage
      : sessionStorage;

    storage.setItem(STORAGE_KEY, serialized);
    setUser(updated);
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isAuthReady,
      login,
      verifyTwoFactor,
      register,
      verifyEmail,
      requestEmailVerification,
      updateRole,
      updateUser,
      impersonateUser,
      stopImpersonation,
      isImpersonating: Boolean(user?.impersonationActive),
      logout,
    }),
    [isAuthReady, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
